import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { platformColor, platformLogo } from "../api/platforms";
import type { LatLon, Platform, StoreCell } from "../api/labels";
import {
  OVERLAP_MAX_BUCKET,
  TIER_ORDER,
  overlapColor,
  overlapLabel,
  tierColor,
  tierLabel,
} from "./tiers";
import { IconCollapseCorners, IconExpandCorners } from "./icons";

type ColorMode = "tier" | "overlap";

// stores serving a cell (this store included, >= 1); cells from endpoints that don't carry
// overlap annotation (the [lat, lon, tier] triple) read as 1 - exclusive by default.
function servingCount(cell: StoreCell): number {
  return typeof cell[3] === "number" && cell[3] > 0 ? cell[3] : 1;
}
function coServingIds(cell: StoreCell): string[] {
  return Array.isArray(cell[4]) ? cell[4] : [];
}

// A focused, single-store map for the store detail page: the serviceable-area
// hull, the probe points where the store was found: coloured by the delivery
// tier the store served at each point (same palette as the tier pills): the
// store LOCATION (its representative centre lat/lon) marked prominently with the
// platform logo, and the routing PROBE point (Phase 2) as a distinct secondary
// marker joined to the location by a thin connector line (so it's obvious the two
// belong to the same store and how far the probe sits from the centre). When the two
// coincide (exclusive store) the probe is folded into the location marker as a bold
// black/yellow hazard ring instead of being dropped. The location marker's own border and
// the probe ring are tinted by the tier served at that point, so the tier isn't lost when
// the opaque marker paints over the tier-coloured cell dot beneath it.
// Fits to whatever geometry exists, else a tight box around the store location.

const LOGO = platformLogo;

// The routing probe's signature hazard pair, named once so the Leaflet layers and the
// legend swatch below can never drift apart. Leaflet takes concrete colours, not CSS
// vars, which is why these are literals here the way tier colours are in `tiers.tsx`.
const PROBE_FILL = "#ffd23f";
const PROBE_RING = "#111111";

// Tier served at a point, read off the nearest probe cell. Used to fold the tier into the
// store-location / probe markers so it survives those opaque markers painting over the
// tier-coloured cell dot beneath them. Capped at ~0.01 deg (~1.1 km): beyond that the
// marker isn't really sitting "on" a probe cell, so we return null (platform-colour fallback).
function nearestTier(pt: LatLon, cells: StoreCell[]): string | null {
  let best: string | null = null;
  let bestD = Infinity;
  for (const [lat, lon, tier] of cells) {
    const d = (lat - pt[0]) ** 2 + (lon - pt[1]) ** 2;
    if (d < bestD) {
      bestD = d;
      best = tier;
    }
  }
  return bestD <= 0.01 * 0.01 ? best : null;
}

export function StoreDetailMap({
  platform,
  storeId,
  anchor,
  probe,
  boundary,
  cells,
  height,
}: {
  platform: Platform;
  storeId?: string; // this store's id - named in the overlap tooltip's serving list
  anchor: LatLon; // store location (logo marker)
  probe?: LatLon | null; // routing probe point; null/zero/equal -> not drawn
  boundary: LatLon[] | null;
  cells: StoreCell[];
  // px height override; the default 460 (CSS) is too short on a page where this map is
  // the left column beside a tall detail panel
  height?: number;
}) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  // The serviceable-area map is unreadable at 460px on a dense store; expanding it is
  // the whole fix. Leaflet caches its container size, so re-measure after the toggle.
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const m = mapRef.current;
    if (m) setTimeout(() => m.invalidateSize(), 60);
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  // overlap annotation is only present on the store-detail read; other callers pass plain
  // [lat, lon, tier] triples. The colour-by toggle only appears when the data supports it.
  const hasOverlap = useMemo(() => cells.some((c) => typeof c[3] === "number"), [cells]);
  const [colorMode, setColorMode] = useState<ColorMode>("tier");
  const mode: ColorMode = hasOverlap ? colorMode : "tier";

  // distinct tiers present, ordered for the legend. Cell tags arrive already
  // canonicalized from the backend (_cell_tier), so they match TIER_ORDER's tokens
  // directly; a non-canonical source would fall through to the `extra` bucket.
  const tiers = useMemo(() => {
    const present = new Set(cells.map((c) => c[2]));
    const known = TIER_ORDER.filter((t) => present.has(t));
    const extra = [...present].filter((t) => t && !TIER_ORDER.includes(t)).sort();
    const out = [...known, ...extra];
    if (present.has("")) out.push("");
    return out;
  }, [cells]);

  // overlap buckets present (1..4+), ascending - the overlap-mode legend.
  const overlapBuckets = useMemo(() => {
    const present = new Set(cells.map((c) => Math.min(OVERLAP_MAX_BUCKET, servingCount(c))));
    return [...present].sort((a, b) => a - b);
  }, [cells]);

  // routing-probe classification (shared by the map draw and the legend key). A probe drawn
  // separately when it differs from the store location; folded into the location marker when it
  // coincides (exclusive store); absent when unset (0,0, not yet re-swept since the split).
  const probePresent = probe != null && (probe[0] !== 0 || probe[1] !== 0);
  const probeSeparate =
    probePresent && !!probe && (probe[0] !== anchor[0] || probe[1] !== anchor[1]);
  const probeCoincident = probePresent && !probeSeparate;

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const m = L.map(elRef.current, { attributionControl: false, preferCanvas: true });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(m);
    mapRef.current = m;
    setTimeout(() => m.invalidateSize(), 0);
    return () => {
      m.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const color = platformColor(platform);
    const drawn = L.layerGroup().addTo(m);

    // serviceable-area hull
    if (boundary && boundary.length >= 3) {
      L.polygon(boundary, {
        color,
        weight: 2.5,
        opacity: 0.95,
        fillColor: color,
        fillOpacity: 0.12,
        dashArray: "5 4",
        interactive: false,
      }).addTo(drawn);
    }

    // probe points where this store was found. Fill by the active colour mode: the delivery
    // tier served there, or (overlap mode) how many stores share the cell. The tooltip always
    // carries both the tier and, when overlap data is present, the co-serving store list.
    for (const cell of cells) {
      const [lat, lon, tier] = cell;
      const n = servingCount(cell);
      const fill = mode === "overlap" ? overlapColor(n) : tierColor(tier);
      let tip = `${tierLabel(tier)} · ${lat.toFixed(4)}, ${lon.toFixed(4)}`;
      if (hasOverlap) {
        if (n <= 1) {
          tip += "<br>exclusive - only this store serves this cell";
        } else {
          const ids = coServingIds(cell);
          const serving = [storeId, ...ids].filter(Boolean) as string[];
          const shown = serving.slice(0, 8).join(", ");
          const more = serving.length > 8 ? `, +${serving.length - 8} more` : "";
          tip += `<br>${n} stores serve this cell: ${shown}${more}`;
        }
      }
      L.circleMarker([lat, lon], {
        radius: 4,
        color: "#fff",
        weight: 1,
        fillColor: fill,
        fillOpacity: 1,
      })
        .bindTooltip(tip, { direction: "top" })
        .addTo(drawn);
    }

    // tier served at the store location (nearest probe cell), folded into the marker so it
    // survives the opaque logo disc painting over the tier-coloured cell dot underneath.
    const anchorTier = nearestTier(anchor, cells);
    const ringColor = anchorTier != null ? tierColor(anchorTier) : color;

    // anchor point, the store's representative location. Its tier-served is carried on the
    // logo's own border (qcs-mk-tier: thickened, tier-coloured, white halo: always visible,
    // unlike a ring hidden behind the 30px logo). When the routing probe coincides we wrap the
    // logo in a bold black+yellow "hazard" ring (the probe's signature) so it stays visible.
    if (probeCoincident) {
      L.circleMarker(anchor, {
        radius: 23,
        color: PROBE_RING,
        weight: 3,
        opacity: 1,
        fill: false,
        interactive: false,
      }).addTo(drawn);
      L.circleMarker(anchor, {
        radius: 23,
        color: PROBE_FILL,
        weight: 3,
        opacity: 1,
        dashArray: "4 6",
        fill: false,
        interactive: false,
      }).addTo(drawn);
    }
    const anchorTierBit = anchorTier != null ? ` · ${tierLabel(anchorTier)}` : "";
    const anchorLabel = probeCoincident
      ? `store location + routing probe (coincident)${anchorTierBit}`
      : `store location${anchorTierBit}`;
    const tierClass = anchorTier != null ? " qcs-mk-tier" : "";
    const borderStyle = anchorTier != null ? `;border-color:${ringColor}` : "";
    L.marker(anchor, {
      icon: L.divIcon({
        className: "",
        iconSize: [30, 30],
        iconAnchor: [15, 15],
        html: `<div class="qcs-mk qcs-mk-anchor${tierClass}" style="background-image:url(${LOGO(
          platform,
        )});background-color:${color}${borderStyle}"></div>`,
      }),
      zIndexOffset: 1000,
    })
      .bindTooltip(`${anchorLabel} · ${anchor[0].toFixed(4)}, ${anchor[1].toFixed(4)}`, {
        direction: "top",
        offset: [0, -14],
      })
      .addTo(drawn);

    // routing probe point + a thin connector to the store location, only when it differs from
    // the location. Its ring is tinted by the tier served there (fallback black) so the tier
    // isn't lost under the marker; the yellow fill keeps it recognizable as the probe.
    if (probeSeparate && probe) {
      const probeTier = nearestTier(probe, cells);
      L.polyline([anchor, probe], {
        color: "#555",
        weight: 1.5,
        opacity: 0.8,
        dashArray: "3 3",
        interactive: false,
      }).addTo(drawn);
      L.circleMarker(probe, {
        radius: 7,
        color: probeTier != null ? tierColor(probeTier) : PROBE_RING,
        weight: 2.5,
        fillColor: PROBE_FILL,
        fillOpacity: 1,
      })
        .bindTooltip(
          `probe point${probeTier != null ? ` · ${tierLabel(probeTier)}` : ""} · ${probe[0].toFixed(
            4,
          )}, ${probe[1].toFixed(4)}`,
          { direction: "top" },
        )
        .addTo(drawn);
    }

    // fit to everything we drew, else a tight box around the store location
    const cellPts: LatLon[] = cells.map((c) => [c[0], c[1]]);
    const pts: LatLon[] = [
      anchor,
      ...(probeSeparate && probe ? [probe] : []),
      ...cellPts,
      ...(boundary ?? []),
    ];
    if (pts.length > 1) {
      m.fitBounds(L.latLngBounds(pts).pad(0.15));
    } else {
      m.setView(anchor, 14);
    }
    setTimeout(() => m.invalidateSize(), 0);

    return () => {
      m.removeLayer(drawn);
    };
  }, [platform, storeId, anchor, probe, boundary, cells, mode, hasOverlap, probeSeparate, probeCoincident]);

  return (
    <div className={`sdetail__mapwrap${fullscreen ? " map--full" : ""}`}>
      <div style={{ position: "relative" }}>
        <div
          ref={elRef}
          className="sdetail__map"
          style={fullscreen ? { height: "calc(100vh - 120px)" } : height ? { height } : undefined}
        />
        <button
          type="button"
          className="scov__icon"
          style={{ position: "absolute", top: 10, right: 10, zIndex: 500 }}
          onClick={() => setFullscreen((v) => !v)}
          title={fullscreen ? "Exit fullscreen (Esc)" : "Expand to fullscreen"}
          aria-label={fullscreen ? "Exit fullscreen" : "Expand to fullscreen"}
        >
          {fullscreen ? <IconCollapseCorners /> : <IconExpandCorners />}
        </button>
      </div>
      {(tiers.length > 0 || probePresent) && (
        <div className="sdetail__legend-bar">
          {hasOverlap && (
            <span className="sdetail__mode">
              Colour by
              <span className="seg">
                <button
                  type="button"
                  className={mode === "tier" ? "on" : ""}
                  onClick={() => setColorMode("tier")}
                >
                  Tier
                </button>
                <button
                  type="button"
                  className={mode === "overlap" ? "on" : ""}
                  onClick={() => setColorMode("overlap")}
                >
                  Overlap
                </button>
              </span>
            </span>
          )}
          <div className="sdetail__legend">
            {mode === "overlap"
              ? overlapBuckets.map((n) => (
                  <span className="sdetail__legend-item" key={`ov${n}`}>
                    <span className="sdetail__legend-dot" style={{ background: overlapColor(n) }} />
                    {overlapLabel(n)}
                  </span>
                ))
              : tiers.map((t) => (
                  <span className="sdetail__legend-item" key={t || "untiered"}>
                    <span className="sdetail__legend-dot" style={{ background: tierColor(t) }} />
                    {tierLabel(t)}
                  </span>
                ))}
            {probePresent && (
              <span className="sdetail__legend-item" key="__probe">
                <span
                  className="sdetail__legend-dot"
                  style={{ background: PROBE_FILL, boxShadow: `0 0 0 2px ${PROBE_RING}` }}
                />
                {probeCoincident ? "routing probe (on store)" : "routing probe"}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
